import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Mail, Phone } from "lucide-react";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", role: "", company_id: "none", notes: "" };

export default function Contacts() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts", search],
    queryFn: async () => (await api.get("/contacts", { params: search ? { search } : {} })).data,
  });
  const { data: companies = [] } = useQuery({
    queryKey: ["companies-all"], queryFn: async () => (await api.get("/companies")).data,
  });
  const coMap = Object.fromEntries(companies.map((c) => [c.id, c.name]));

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = { ...body, company_id: body.company_id === "none" ? null : body.company_id };
      if (body.id) return (await api.patch(`/contacts/${body.id}`, payload)).data;
      return (await api.post("/contacts", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); setOpen(false); toast.success("Saved"); },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/contacts/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); toast.success("Deleted"); },
  });

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle="People at customer and lead companies."
        breadcrumb={["CRM", "Contacts"]}
        actions={
          <Button data-testid="new-contact-btn" onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New contact
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        <Card className="rounded-sm border-slate-200 industrial-shadow">
          <div className="p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="relative max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="contact-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts..." className="pl-9 h-10 rounded-sm" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {contacts.map((c) => (
              <div key={c.id} className="border border-slate-200 p-4 hover:border-cyan-300 transition-colors group" data-testid={`contact-card-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-semibold text-slate-900">{c.first_name} {c.last_name}</div>
                    {c.role && <div className="text-xs text-slate-500 mt-0.5">{c.role}</div>}
                  </div>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing({ ...c, company_id: c.company_id || "none" }); setOpen(true); }} data-testid={`edit-contact-${c.id}`}><Pencil size={12} /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => del.mutate(c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 size={12} /></Button>
                  </div>
                </div>
                {c.company_id && <div className="text-xs text-cyan-600 font-medium mb-2">{coMap[c.company_id] || ""}</div>}
                <div className="space-y-1 text-xs text-slate-600">
                  {c.email && <div className="flex items-center gap-1.5"><Mail size={12} /> {c.email}</div>}
                  {c.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {c.phone}</div>}
                </div>
              </div>
            ))}
            {contacts.length === 0 && <div className="col-span-full py-12 text-center text-slate-400 text-sm">No contacts</div>}
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl" data-testid="contact-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required><Input data-testid="contact-first-name" value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></Field>
                <Field label="Last name" required><Input value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></Field>
                <Field label="Email"><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
                <Field label="Phone"><Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></Field>
                <Field label="Role"><Input value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })} /></Field>
                <Field label="Company">
                  <Select value={editing.company_id || "none"} onValueChange={(v) => setEditing({ ...editing, company_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Notes"><Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-contact-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
